/**
 * Creates an object that represents the list map.
 * List map is the object can be useful for dropdown lists
 * or any objects that contain list of unique values (arrays)
 * per property / row.
 *
 * It can be useful when you have to keep some
 * currently selected value data in memory.
 *
 * Eg you have the list of companies. Each company have its
 * own list of probable categories. Companies are listed
 * in dropdown list and can be changed. When you change the
 * company the list of possible categories should be changed
 * to appropriate values in another dropdown list.
 * But when users switch between different companies again
 * and again you will have to store this kind of relation
 * somewhere in memory.
 * Then you could do the following:
 * @example
 * ```javascript
 *   const companyToCategoriesMap = makeListMap({
 *     companyA: ['affiliate marketing', 'b2b'],
 *     companyB: ['real estate', 'restaraunts']
 *   });
 *   // jq-like pseudo API
 *   $companyDropdown.on('change', companyId => {
 *     companyToCategoriesMap.select(companyId);
 *   })
 *
 *   $categoryMultiSelect.on('click', categoryIds => {
 *     companyToCategoriesMap.add(categoryIds);
 *
 *     $categoriesToSelect.appendChild(`<p>${companyToCategoriesMap.get().join(', ')}</p>`)
 *   })
 *
 * ```
 *
 * @param {Object=} initialList             The keys are any valid object keys
 *                                          and the primitive values are the arrays of
 *                                          any variable types
 *
 * @param {number=} initialActiveRowId
 *
 * @return {
 * {
 *   get: (function(): Array),
 *   replace: (function(Array.<*>): Array.<*>),
 *   add: (function(Array.<*>, ?number)),
 *   select: (function(number))}
 * }
 */
const makeListMap = (initialList = {}, initialActiveRowId) => {
    let listMap = initialList,
        activeRowId = initialActiveRowId || Object.keys(initialList)[0],
        rowKey;

    if (activeRowId) {
        for (rowKey in initialList) {
            if (initialList.hasOwnProperty(rowKey)) {
                initialList[rowKey] = [...new Set(initialList[rowKey])];
            }
        }
    }

    const activeRow = () => listMap[activeRowId] || [];

    return {
        /**
         * @return {Array.<*>}
         */
        get: () => activeRowId ? activeRow() : [],
        /**
         * Replaces active row values
         * @param {Array.<*>} replacement
         */
        replace: (replacement) => listMap[activeRowId] = replacement,
        /**
         *
         * @param {Array.<*>} newValues
         * @param {number=}   rowId
         */
        add: (newValues, rowId) => {
            rowId = rowId || activeRowId;

            if (!rowId) {
                throw new Error(
                    `No active row specified yet. Passing row id as 
                     a second argument is required in this case.`
                );
            }

            listMap[rowId] = [...new Set([...activeRow(), ...newValues])];
            activeRowId = rowId;
        },

        select: newActiveRowId => {
            activeRowId = newActiveRowId;
            listMap[activeRowId] = listMap.hasOwnProperty(activeRowId) ?  activeRow() : [];
        },

        isInitialized: () => activeRowId !== undefined,

        initialize: (initialListMap, initialRowId) => {
            activeRowId = initialRowId;
            listMap = [...new Set(...initialListMap)];
        },

        dump: () => listMap
    };
};

export default makeListMap;


// WEBPACK FOOTER //
// ./web/src/common/listMap.js